import { nip19 } from 'nostr-tools';

export type ContentToken =
  | { type: 'text'; value: string }
  | { type: 'image'; url: string }
  | { type: 'video'; url: string }
  | { type: 'link'; url: string }
  | { type: 'mention'; pubkey: string; raw: string }
  | { type: 'nostr-ref'; raw: string }
  | { type: 'hashtag'; tag: string };

const IMAGE_EXT = /\.(jpg|jpeg|png|gif|webp|avif|svg)(\?[^\s]*)?$/i;
const VIDEO_EXT = /\.(mp4|webm|ogg|mov|m4v)(\?[^\s]*)?$/i;

// Single master regex capturing: URL, nostr: URI, bare nip19 id, hashtag
const TOKEN_REGEX =
  /((?:https?):\/\/[^\s<]+)|nostr:(npub1|note1|nprofile1|nevent1|naddr1)([023456789acdefghjklmnpqrstuvwxyz]+)|@?(npub1|note1|nprofile1|nevent1|naddr1)([023456789acdefghjklmnpqrstuvwxyz]+)|(#[\p{L}\p{N}_]+)/giu;

/**
 * Only allow https URLs — drop anything else to keep the CSP-safe render
 * invariant intact (no javascript:, data:, etc).
 */
function isSafeUrl(raw: string): boolean {
  try {
    const u = new URL(raw);
    return u.protocol === 'https:' || u.protocol === 'http:';
  } catch {
    return false;
  }
}

export function tokenizeContent(content: string): ContentToken[] {
  const tokens: ContentToken[] = [];
  let lastIndex = 0;

  // Reset regex state just in case
  TOKEN_REGEX.lastIndex = 0;

  let match: RegExpExecArray | null;
  while ((match = TOKEN_REGEX.exec(content)) !== null) {
    const [full, url, nostrPrefix, nostrBody, barePrefix, bareBody, hashtag] = match;
    const start = match.index;

    if (start > lastIndex) {
      tokens.push({ type: 'text', value: content.slice(lastIndex, start) });
    }

    if (url) {
      if (!isSafeUrl(url)) {
        tokens.push({ type: 'text', value: url });
      } else if (IMAGE_EXT.test(url)) {
        tokens.push({ type: 'image', url });
      } else if (VIDEO_EXT.test(url)) {
        tokens.push({ type: 'video', url });
      } else {
        tokens.push({ type: 'link', url });
      }
    } else if (nostrPrefix && nostrBody) {
      const bech32 = `${nostrPrefix}${nostrBody}`;
      tokens.push(decodeNostrToken(bech32, full));
    } else if (barePrefix && bareBody) {
      const bech32 = `${barePrefix}${bareBody}`;
      tokens.push(decodeNostrToken(bech32, full));
    } else if (hashtag) {
      tokens.push({ type: 'hashtag', tag: hashtag.slice(1) });
    }

    lastIndex = start + full.length;
  }

  if (lastIndex < content.length) {
    tokens.push({ type: 'text', value: content.slice(lastIndex) });
  }

  return tokens;
}

function decodeNostrToken(bech32: string, raw: string): ContentToken {
  try {
    const decoded = nip19.decode(bech32);
    if (decoded.type === 'npub') {
      return { type: 'mention', pubkey: decoded.data, raw: bech32 };
    }
    if (decoded.type === 'nprofile') {
      return { type: 'mention', pubkey: decoded.data.pubkey, raw: bech32 };
    }
  } catch {
    // fall through
  }
  return { type: 'nostr-ref', raw };
}

export interface ImetaEntry {
  url: string;
  mime?: string;
  dim?: string;
  blurhash?: string;
}

export function parseImetaMap(tags: string[][]): Map<string, ImetaEntry> {
  const map = new Map<string, ImetaEntry>();
  for (const tag of tags) {
    if (tag[0] !== 'imeta') continue;
    const entry: Record<string, string> = {};
    for (let i = 1; i < tag.length; i++) {
      const part = tag[i];
      const sp = part.indexOf(' ');
      if (sp === -1) continue;
      entry[part.slice(0, sp)] = part.slice(sp + 1);
    }
    if (entry.url && isSafeUrl(entry.url)) {
      map.set(entry.url, {
        url: entry.url,
        mime: entry.m,
        dim: entry.dim,
        blurhash: entry.blurhash,
      });
    }
  }
  return map;
}
